import { useState, useCallback, useEffect } from "react";
import UsersService from "../../../services/UsersService";
import RoleService from "../../../services/RoleService";
import UbicacionService from "../../../services/UbicacionService";
import InstitucionEService from "../../../services/InstitucionE";

export const useGestorUsuarios = ({
    page,
    rowsPerPage,
    filters,
    sort,
}) => {
    const {
        search = "",
        rolId = "",
        institucionId = "",
        nivelModalidad = "",
        fechaDesde = "",
        fechaHasta = "",
    } = filters;
    const {
        orderBy = "created_at",
        orderDir = "desc",
    } = sort;

    // Estados de Datos
    const [usuarios, setUsuarios] = useState([]);
    const [roles, setRoles] = useState([]);
    const [totalUsuarios, setTotalUsuarios] = useState(0);
    const [institucionesFiltro, setInstitucionesFiltro] = useState([]);
    const [ubicaciones, setUbicaciones] = useState({
        regiones: [],
        provincias: [],
        distritos: [],
        instituciones: [],
    });

    // Estados de Carga (Loading)
    const [loading, setLoading] = useState({
        usuarios: false,
        roles: false,
        regiones: false,
        provincias: false,
        distritos: false,
        instituciones: false,
        institucionesFiltro: false,
        crearInstitucion: false,
    });

    // Estado de Feedback (Snackbar)
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showMsg = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // --- Lógica de Usuarios ---
    const cargarUsuarios = useCallback(async () => {
        setLoading((prev) => ({ ...prev, usuarios: true }));
        try {
            const skip = page * rowsPerPage;
            const response = await UsersService.obtenerUsersConInstituciones({
                skip,
                limit: rowsPerPage,
                q: search || undefined,
                rol_id: rolId || undefined,
                institucion_id: institucionId || undefined,
                nivel_modalidad: nivelModalidad || undefined,
                fecha_desde: fechaDesde || undefined,
                fecha_hasta: fechaHasta || undefined,
                order_by: orderBy,
                order_dir: orderDir,
            });

            setUsuarios(Array.isArray(response?.items) ? response.items : []);
            setTotalUsuarios(Number(response?.total || 0));
        } catch (error) {
            showMsg("Error al cargar la lista de usuarios", "error");
        } finally {
            setLoading((prev) => ({ ...prev, usuarios: false }));
        }
    }, [page, rowsPerPage, search, rolId, institucionId, nivelModalidad, fechaDesde, fechaHasta, orderBy, orderDir, showMsg]);

    // --- Lógica de Roles y Ubicaciones ---
    const cargarRoles = async () => {
        setLoading((prev) => ({ ...prev, roles: true }));
        try {
            const data = await RoleService.obtenerRoles();
            setRoles(Array.isArray(data) ? data : []);
        } finally {
            setLoading((prev) => ({ ...prev, roles: false }));
        }
    };

    const cargarRegiones = async () => {
        setLoading((prev) => ({ ...prev, regiones: true }));
        try {
            const data = await UbicacionService.listarRegiones();
            setUbicaciones(prev => ({ ...prev, regiones: data || [] }));
        } finally {
            setLoading((prev) => ({ ...prev, regiones: false }));
        }
    };

    const cargarProvincias = async (regionId) => {
        setLoading((prev) => ({ ...prev, provincias: true }));
        try {
            const data = await UbicacionService.listarProvincias(regionId);
            setUbicaciones(prev => ({ ...prev, provincias: data || [], distritos: [], instituciones: [] }));
        } finally {
            setLoading((prev) => ({ ...prev, provincias: false }));
        }
    };

    const cargarDistritos = async (provinciaId) => {
        setLoading((prev) => ({ ...prev, distritos: true }));
        try {
            const data = await UbicacionService.listarDistritos(provinciaId);
            setUbicaciones(prev => ({ ...prev, distritos: data || [], instituciones: [] }));
        } finally {
            setLoading((prev) => ({ ...prev, distritos: false }));
        }
    };

    const cargarInstituciones = async (distritoId) => {
        setLoading((prev) => ({ ...prev, instituciones: true }));
        try {
            const resp = await InstitucionEService.listarInstituciones({ codigo_ubigeo: distritoId });
            setUbicaciones(prev => ({ ...prev, instituciones: resp?.data || [] }));
        } finally {
            setLoading((prev) => ({ ...prev, instituciones: false }));
        }
    };

    // --- Acciones de Escritura ---
    const eliminarUsuario = async (id) => {
        try {
            await UsersService.eliminarUsuario(id);
            showMsg(`Usuario ${id} eliminado correctamente`);
            await cargarUsuarios();
            return true;
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo eliminar", "error");
            return false;
        }
    };

    const guardarUsuario = async (formData, isEdit) => {
        try {
            if (isEdit) {
                const usuarioActualizado = await UsersService.actualizarUsuario(formData.id, {
                    id: formData.usuario,
                    nombres: formData.nombre,
                    apellidos: formData.apellido,
                });
                showMsg("Usuario actualizado correctamente");
                await cargarUsuarios();
                return { success: true, data: usuarioActualizado };
            } else {
                const usuarioCreado = await UsersService.crearUsuarioBase({
                    dni: formData.usuario,
                    nombres: formData.nombre,
                    apellidos: formData.apellido,
                    is_admin: false,
                });
                showMsg("Usuario creado correctamente");
                await cargarUsuarios();
                return { success: true, data: usuarioCreado };
            }
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo guardar el usuario.", "error");
            return { success: false };
        }
    };

    const resetearPasswordUsuario = async (usuario) => {
        try {
            const response = await UsersService.resetearPasswordUsuario(usuario.id);
            showMsg(`La contraseña de ${usuario.nombres.split(" ")[0]} ${usuario.apellidos.split(" ")[0]} fue restablecida a su DNI`);
            await cargarUsuarios();
            return { success: true, data: response };
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo resetear la contraseña.", "error");
            return { success: false };
        }
    };

    const asignarInstitucion = async (assignData) => {
        try {
            await UsersService.vincularUsuarioInstitucion({
                usuario_id: assignData.usuario_id,
                institucion_id: assignData.institucion_id,
                rol_id: Number(assignData.rol_id),
            });
            showMsg("Institución asignada correctamente");
            await cargarUsuarios();
            return true;
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo asignar la institución.", "error");
            return false;
        }
    };

    const desvincularInstitucionUsuario = async (usuarioId, institucionId) => {
        try {
            await UsersService.desvincularUsuarioInstitucion(usuarioId, institucionId);
            showMsg("Vínculo institucional eliminado correctamente");
            await cargarUsuarios();
            return true;
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo desvincular al usuario de la institución.", "error");
            return false;
        }
    };

    const cargarInstitucionesFiltro = async () => {
        setLoading((prev) => ({ ...prev, institucionesFiltro: true }));
        try {
            const response = await InstitucionEService.listarInstituciones();
            setInstitucionesFiltro(Array.isArray(response?.data) ? response.data : []);
        } finally {
            setLoading((prev) => ({ ...prev, institucionesFiltro: false }));
        }
    };

    const actualizarVinculoInstitucion = async (assignData) => {
        try {
            await UsersService.actualizarVinculoUsuarioInstitucion(
                assignData.usuario_id,
                assignData.original_institucion_id,
                {
                    institucion_id: assignData.institucion_id,
                    rol_id: Number(assignData.rol_id),
                }
            );
            showMsg("Vínculo institucional actualizado correctamente");
            await cargarUsuarios();
            return true;
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo actualizar el vínculo institucional.", "error");
            return false;
        }
    };

    const crearInstitucion = async (institutionData) => {
        setLoading((prev) => ({ ...prev, crearInstitucion: true }));
        try {
            const institucionCreada = await InstitucionEService.crearInstitucion(institutionData);
            showMsg("Institución creada correctamente");
            await Promise.all([
                cargarInstitucionesFiltro(),
                institutionData?.ubicacion_id ? cargarInstituciones(institutionData.ubicacion_id) : Promise.resolve(),
            ]);
            return { success: true, data: institucionCreada };
        } catch (error) {
            showMsg(error?.response?.data?.detail || "No se pudo crear la institución.", "error");
            return { success: false };
        } finally {
            setLoading((prev) => ({ ...prev, crearInstitucion: false }));
        }
    };

    // Inicialización
    useEffect(() => {
        cargarRoles();
        cargarInstitucionesFiltro();
    }, []);

    useEffect(() => {
        cargarUsuarios();
    }, [cargarUsuarios]);

    return {
        usuarios,
        roles,
        totalUsuarios,
        institucionesFiltro,
        ubicaciones,
        loading,
        snackbar,
        setSnackbar,
        acciones: {
            cargarUsuarios,
            cargarInstitucionesFiltro,
            cargarRegiones,
            cargarProvincias,
            cargarDistritos,
            cargarInstituciones,
            eliminarUsuario,
            guardarUsuario,      
            resetearPasswordUsuario,
            asignarInstitucion,  
            actualizarVinculoInstitucion,
            desvincularInstitucionUsuario,
            crearInstitucion,
            showMsg
        }
    };
};
